import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    TextField,
    Button,
    useTheme,
    Stack,
    Snackbar,
    Alert,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { createClient } from '../../services/clients-service';

export interface AddClientPanelProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const AddClientPanel: React.FC<AddClientPanelProps> = ({ open, onClose, onSuccess }) => {
    const theme = useTheme();
    const [clientName, setClientName] = useState('');
    const [subdomain, setSubdomain] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [clientNameError, setClientNameError] = useState('');
    const [subdomainError, setSubdomainError] = useState('');
    const [contactEmailError, setContactEmailError] = useState('');
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    useEffect(() => {
        if (open) {
            setClientName('');
            setSubdomain('');
            setContactEmail('');
            setClientNameError('');
            setSubdomainError('');
            setContactEmailError('');
            setSubmitError(null);
        }
    }, [open]);

    const validate = (): boolean => {
        let valid = true;
        if (!clientName.trim()) {
            setClientNameError('Client name is required');
            valid = false;
        } else {
            setClientNameError('');
        }
        if (!subdomain.trim()) {
            setSubdomainError('Subdomain is required');
            valid = false;
        } else {
            setSubdomainError('');
        }
        if (contactEmail.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(contactEmail.trim())) {
                setContactEmailError('Invalid email format');
                valid = false;
            } else {
                setContactEmailError('');
            }
        } else {
            setContactEmailError('');
        }
        return valid;
    };

    const handleSubmit = async () => {
        setSubmitError(null);
        if (!validate()) return;

        setIsSaving(true);
        try {
            await createClient({
                ClientName: clientName.trim(),
                Subdomain: subdomain.trim(),
                ContactEmail: contactEmail.trim() || undefined,
            });
            setSnackbar({ open: true, message: 'Client created successfully', severity: 'success' });
            onClose();
            onSuccess?.();
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { message?: string } } };
            const message = ax?.response?.data?.message ?? (err instanceof Error ? err.message : 'Failed to create client');
            setSubmitError(message);
            if (message.toLowerCase().includes('subdomain')) {
                setSubdomainError(message);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 420 },
                        bgcolor: theme.palette.background.paper,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                    },
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box
                        sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                            Create client
                        </Typography>
                        <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }} aria-label="Close">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Client name"
                                required
                                value={clientName}
                                onChange={(e) => {
                                    setClientName(e.target.value);
                                    setClientNameError('');
                                }}
                                error={!!clientNameError}
                                helperText={clientNameError}
                                disabled={isSaving}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: theme.palette.background.default,
                                    },
                                }}
                            />
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <Typography component="span" variant="body2" sx={{ color: theme.palette.text.primary }}>
                                        Subdomain <span style={{ color: theme.palette.error.main }}>*</span>
                                    </Typography>
                                    <Tooltip
                                        title="The subdomain uniquely identifies this client and is used in URLs (e.g. acme.yourplatform.com). It cannot be changed later and must be unique across all clients. Use only letters, numbers, and hyphens."
                                        placement="top"
                                        arrow
                                    >
                                        <IconButton size="small" sx={{ color: theme.palette.text.secondary, p: 0.25 }} aria-label="Why subdomain matters">
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <TextField
                                    fullWidth
                                    value={subdomain}
                                    onChange={(e) => {
                                        setSubdomain(e.target.value.replace(/\s/g, '').toLowerCase());
                                        setSubdomainError('');
                                    }}
                                    error={!!subdomainError}
                                    helperText={subdomainError}
                                    disabled={isSaving}
                                    placeholder="e.g. acme"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: theme.palette.background.default,
                                        },
                                    }}
                                />
                            </Box>
                            <TextField
                                fullWidth
                                label="Contact email (optional)"
                                type="email"
                                value={contactEmail}
                                onChange={(e) => {
                                    setContactEmail(e.target.value);
                                    setContactEmailError('');
                                }}
                                error={!!contactEmailError}
                                helperText={contactEmailError}
                                disabled={isSaving}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: theme.palette.background.default,
                                    },
                                }}
                            />

                            {submitError && (
                                <Alert severity="error" onClose={() => setSubmitError(null)}>
                                    {submitError}
                                </Alert>
                            )}

                            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
                                <Button variant="outlined" onClick={onClose} disabled={isSaving} sx={{ textTransform: 'none' }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={isSaving}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {isSaving ? 'Creating…' : 'Create client'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            </Drawer>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddClientPanel;
